import React, { useCallback, useState } from "react";
import { AxiosResponse } from "axios";
import { FormattedMessage } from "react-intl";
import { useDropzone } from "react-dropzone";
import { Alert, Button } from "react-bootstrap";
import axios from "../../../http/customAxios";
import { createExcelFileDownloadFromRawData } from "../../../lib/supportFunctions";
import { IConfig, useConfig } from "../../../lib/config";
import { messages } from "./messages";
import { Container, ButtonContainer, DropzoneContainer } from "./styles";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface PromotionEditByExcelProps {
  id: string;
  propertyTicker: string;
  onHandleImportResult: (data: any) => void;
}

interface OperationError {
  message: string;
}

export default function PromotionEditByExcel({ id, propertyTicker, onHandleImportResult }: PromotionEditByExcelProps) {
  const configuration: IConfig = useConfig();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);

  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleImport = useCallback(async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    setError(undefined);
    try {
      const url = `${configuration.apiEndpoint}/api/backoffice/${propertyTicker}/promotions/${id}/xlsx`;
      const response: AxiosResponse<any> = await axios.post(url, formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      });
      void onHandleImportResult(response.data);
    } catch (err) {
      setError({ message: "Access denied" });
    } finally {
      setLoading(false);
    }
  }, [configuration.apiEndpoint, file, id, onHandleImportResult, propertyTicker]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${configuration.apiEndpoint}/api/backoffice/${propertyTicker}/promotions/${id}/xlsx`;
      const response = await axios.get(url, { responseType: "blob" });
      const filename = response.headers["content-disposition"].split(";")[1].trim().split("=")[1];
      createExcelFileDownloadFromRawData(response.data, filename);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [configuration.apiEndpoint, id, propertyTicker]);

  return (
    <>
      <Container>
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          {file ? <div>{file.name}</div> : <FormattedMessage {...messages.dropHere} />}
        </DropzoneContainer>
      </Container>
      {error && (
        <Alert variant="danger">
          <strong>
            <FormattedMessage {...messages.saveError} />
          </strong>
        </Alert>
      )}
      <ButtonContainer>
        <Button variant="primary" type="button" onClick={handleImport}>
          <FormattedMessage {...messages.import} />
        </Button>
        <Button variant="primary" type="button" onClick={handleExport} disabled={isLoading} className="min-vw-30">
          {isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : <FormattedMessage {...messages.exportToExcel} />}
        </Button>
      </ButtonContainer>
    </>
  );
}